/**
 * useRoomSchedule — owns the pre-show / waiting-room time math for
 * LiveRoomScreen.
 *
 * Carved out of LiveRoomScreen.tsx in AMB-232 slice 2 (LiveRoom batch).
 * Bundles:
 *  - the `nowTick` state that forces a render once per second while
 *    a countdown is on screen,
 *  - the gated tick interval (runs ONLY while in the waiting-room
 *    or Active-but-too-early window — never wastes a setInterval
 *    after media starts),
 *  - all the derived booleans / millisecond values the JSX consumes
 *    to decide between the live grid, the waiting-room layout, and
 *    the «practice starts in X:XX» overlay,
 *  - `formatCountdown` exposed inline so the host JSX has a one-call
 *    surface and we don't duplicate the conversion in two render
 *    sites.
 *
 * Returns `nowTick` so the host's play/pause media-element effect can
 * keep it in its deps array — that effect needs to re-check
 * `Date.now() < anchor` every second while in pre-show.
 *
 * The host still owns `startingRoom` + `handleManualStart` because the
 * latter calls `setRoom` (which lives on the host).
 */
import { useEffect, useState } from 'react';
import { startSecondTick } from '../../utils/secondTick';
import { serverNow } from '../../utils/serverClock';
import type { RoomResponse } from '../../types/auth';

const FIVE_MIN_MS = 5 * 60 * 1000;

/**
 * CORE-1400 — сколько предстарта клэмп на плановое время готов покрыть. Это то
 * же окно, в котором ведущему доступна кнопка «Запустить сейчас»
 * (`canManualStart`), и держать их врозь нельзя: зал ждёт ровно столько,
 * сколько ведущему разрешено стартовать заранее.
 */
export const PRE_START_CLAMP_MS = FIVE_MIN_MS;

interface UseRoomScheduleParams {
    room: RoomResponse | null;
    /** From `room.hostUserId === user.id` on the host — gates
     *  `canManualStart`. */
    isRoomHost: boolean;
}

export interface UseRoomScheduleReturn {
    scheduledAtMs: number;
    msUntilScheduled: number;
    /** True while the BE has the room in `Scheduled` state with a non-
     *  zero scheduledAt. The host can be in this state at any point
     *  before scheduledAt OR for the ≤60 s window after, while the BE
     *  cron lags the Status flip. */
    isWaitingRoom: boolean;
    mediaStartedMs: number;
    /** Canonical playback anchor — clamps a host's pre-start to the
     *  scheduled moment so participants entering at the announced time
     *  always see 00:00. CORE-1400: the clamp is bounded by the 5-min
     *  pre-start window it was written for — a room started earlier than
     *  that anchors on its real start, so a running broadcast is never
     *  held behind its own schedule. */
    effectiveStartMs: number;
    /** True when status='Active' but `Date.now()` is still before the
     *  anchor (rev 4 product call: host pre-starting in the
     *  scheduledAt-5min window should not roll media yet). Never true
     *  once the server-reported start is more than that window ahead of
     *  schedule (CORE-1400). */
    isTooEarly: boolean;
    /** Milliseconds until media actually starts (zero when not isTooEarly). */
    msUntilPracticeStart: number;
    /** Either pre-scheduledAt OR Active-but-too-early — the host renders
     *  the unified waiting-room view in this period. */
    isPreShow: boolean;
    /** The countdown number is only meaningful while time is positive;
     *  once scheduledAt arrives, the waiting-room view swaps to
     *  «запускается…» while the BE cron flips status to Active. */
    isCountdownActive: boolean;
    /** Host's «Запустить сейчас» button is gated to the last 5 min
     *  before scheduledAt (and stays clickable after, so a stuck cron
     *  can be unblocked manually). */
    canManualStart: boolean;
    /** Re-render tick that bumps every second while a countdown is
     *  visible. Host's media-element effect deps on this so its
     *  `Date.now() < anchor` gate re-evaluates each tick. */
    nowTick: number;
    /** Pretty-prints a duration (ms) as «Nд Mч Lм», «Hч Mм SSс» or
     *  «MM:SS» depending on size. Used by both the waiting-room and
     *  the «practice starts in X:XX» overlay. */
    formatCountdown: (ms: number) => string;
}

/**
 * CORE-1400 — «участник видит ожидание вместо идущего эфира».
 *
 * Якорь предстарта. Клэмп на `scheduledAtMs` писался под окно в 5 минут
 * (rev 4): ведущий жмёт «Запустить» перед самым началом, а участник, вошедший
 * к объявленному времени, всё равно видит 00:00. Ограничения сверху у клэмпа
 * не было — `Math.max(mediaStartedMs, scheduledAtMs)` ставил ПЛАНОВОЕ время
 * выше ФАКТИЧЕСКОГО старта на любом расстоянии. Комната, запущенная за два
 * часа до планового момента (живой сценарий: «создали анонс на потом, запустили
 * сразу, чтобы проверить»), держала КАЖДОГО участника на экране ожидания эти
 * два часа: `isPreShow` подменяет весь живой интерфейс ранним возвратом на
 * `WaitingRoomView` в LiveRoomScreen — отсюда «только режим ожидания» и «у меня
 * и видео нет» при идущем эфире, и отсчёт больше часа на скриншоте.
 *
 * Часовой пояс к этому отношения не имеет, хотя в отчёте подозревали его:
 * бэкенд отдаёт метки с `Z` (`2026-09-26T05:00:00Z`), такая строка парсится
 * одинаково в любом поясе и в Safari тоже, а сравниваются здесь абсолютные
 * миллисекунды. Тесты пояса это фиксируют.
 *
 * Правило: за пределами окна предстарта плановое время уступает фактическому.
 * Если сервер говорит, что эфир идёт, расписание не вправе держать участника
 * снаружи. Внутри окна поведение прежнее — в том числе клэмп «вошедший вовремя
 * видит 00:00».
 *
 * `mediaStartedAtUtc` есть не у всякой комнаты (живая сессия без медиа его не
 * ставит), поэтому фактическим стартом считается более поздняя из
 * `mediaStartedAtUtc` / `startedAtUtc`: важно само «эфир уже идёт».
 */
export interface PreShowAnchorInput {
    /** `room.status` as the BE reports it; `null` when there is no room yet. */
    status: string | null;
    scheduledAtMs: number;
    mediaStartedMs: number;
    startedAtMs: number;
    now: number;
}

export interface PreShowAnchorResult {
    effectiveStartMs: number;
    isTooEarly: boolean;
}

export function resolvePreShowAnchor(input: PreShowAnchorInput): PreShowAnchorResult {
    const { status, scheduledAtMs, mediaStartedMs, startedAtMs, now } = input;
    const actualStartMs = Math.max(mediaStartedMs, startedAtMs);
    const preStartLeadMs = actualStartMs > 0 ? scheduledAtMs - actualStartMs : 0;
    const effectiveStartMs =
        preStartLeadMs > PRE_START_CLAMP_MS ? actualStartMs : Math.max(mediaStartedMs, scheduledAtMs);
    const isTooEarly = status === 'Active' && effectiveStartMs > 0 && now < effectiveStartMs;
    return { effectiveStartMs, isTooEarly };
}

export function useRoomSchedule({ room, isRoomHost }: UseRoomScheduleParams): UseRoomScheduleReturn {
    const [nowTick, setNowTick] = useState(0);

    const scheduledAtMs = room?.scheduledAtUtc ? new Date(room.scheduledAtUtc).getTime() : 0;
    const msUntilScheduled = scheduledAtMs > 0 ? scheduledAtMs - serverNow() : 0;
    const isWaitingRoom = !!room && room.status === 'Scheduled' && scheduledAtMs > 0;
    const canManualStart = isRoomHost && isWaitingRoom && msUntilScheduled <= FIVE_MIN_MS;

    const mediaStartedMs = room?.mediaStartedAtUtc ? new Date(room.mediaStartedAtUtc).getTime() : 0;
    const { effectiveStartMs, isTooEarly } = resolvePreShowAnchor({
        status: room?.status ?? null,
        scheduledAtMs,
        mediaStartedMs,
        startedAtMs: room?.startedAtUtc ? new Date(room.startedAtUtc).getTime() : 0,
        now: serverNow(),
    });
    const msUntilPracticeStart = isTooEarly ? Math.max(0, effectiveStartMs - serverNow()) : 0;
    const isPreShow = isWaitingRoom || isTooEarly;
    const isCountdownActive = isPreShow && msUntilScheduled > 0;

    // Tick once a second so the countdown re-renders. Runs while either
    // the waiting-room view is visible OR the room is Active-but-tooEarly
    // (host pre-started inside the 5-min window — we still display a
    // «starts in X:XX» overlay until the real scheduledAt moment).
    //
    // CORE-1146 — `startSecondTick`, not `setInterval`: it aims each tick at
    // the next whole second instead of «1000 ms after the previous callback
    // returned», so the render this tick costs cannot push the next one past a
    // second boundary and make the countdown skip a number. It also ticks the
    // moment the tab/app comes back to the foreground, so a throttled or frozen
    // background never leaves a stale number on screen — every value here is
    // recomputed from `scheduledAtUtc` against `Date.now()` on render, so one
    // tick is the whole catch-up.
    useEffect(() => {
        if (!isWaitingRoom && !isTooEarly) return;
        return startSecondTick(() => setNowTick(n => n + 1));
    }, [isWaitingRoom, isTooEarly]);

    return {
        scheduledAtMs,
        msUntilScheduled,
        isWaitingRoom,
        mediaStartedMs,
        effectiveStartMs,
        isTooEarly,
        msUntilPracticeStart,
        isPreShow,
        isCountdownActive,
        canManualStart,
        nowTick,
        formatCountdown,
    };
}

/** Pure: pretty-prints a duration (ms). Exported standalone for unit
 *  testing and for callers that don't need the hook's per-second tick
 *  but want the same formatting. */
export function formatCountdown(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (days > 0) return `${days}д ${hours}ч ${minutes}м`;
    if (hours > 0) return `${hours}ч ${minutes}м ${seconds.toString().padStart(2, '0')}с`;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
